/**
 * Allys Project Hero Carousel
 * 
 * Allys-themed wrapper for the core ProjectHeroCarousel component.
 * Features modern Allys branding with sophisticated gradient backgrounds.
 */

'use client';

import { ProjectHeroCarousel } from '../../../../src/components/projects';
import type { PropertyDevelopmentProject } from '../../../../src/lib/api/clients/projects';

export interface AllysProjectHeroCarouselProps {
  /** Featured projects to display */
  projects: PropertyDevelopmentProject[];
  /** Optional title override */
  title?: string;
  /** Optional subtitle override */
  subtitle?: string;
  /** Optional badge text override */
  badgeText?: string;
  /** Stats to display */
  stats?: Array<{
    label: string;
    value: string | number;
  }>;
  /** Optional CTA text */
  ctaText?: string;
  /** Optional CTA link */
  ctaLink?: string;
  /** Messages for translations */
  messages?: Record<string, unknown>;
  /** Current locale */
  locale?: string;
  /** Max projects to show in carousel */
  maxProjects?: number;
  /** On project click handler */
  onProjectClick?: (project: PropertyDevelopmentProject) => void;
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * AllysProjectHeroCarousel
 * 
 * Wraps the core ProjectHeroCarousel with Allys-specific styling:
 * - Sophisticated gradient background
 * - Allys brand colors
 * - Premium typography
 * - Smooth animations
 */
export function AllysProjectHeroCarousel({
  projects,
  title,
  subtitle,
  badgeText,
  stats,
  ctaText,
  ctaLink,
  messages,
  locale = 'en',
  maxProjects = 5,
  onProjectClick,
}: AllysProjectHeroCarouselProps) {
  // Extract translations safely
  const heroMsg = messages && typeof messages === 'object' && 'projectHero' in messages 
    ? (messages.projectHero as Record<string, unknown>) 
    : undefined;
  
  const projectsMsg = messages && typeof messages === 'object' && 'projects' in messages
    ? (messages.projects as Record<string, unknown>)
    : undefined;
    
  const cardMsg = projectsMsg && typeof projectsMsg === 'object' && 'card' in projectsMsg
    ? (projectsMsg.card as Record<string, unknown>)
    : undefined;
  
  const heroTitle = title || safeGetString(heroMsg?.title, 'Signature Development Projects');
  const heroSubtitle = subtitle || safeGetString(heroMsg?.subtitle, 'Discover exceptional property developments that redefine modern living. Each project is thoughtfully designed to deliver outstanding quality and lifestyle.');
  const heroBadge = badgeText || safeGetString(heroMsg?.badge, 'Featured Projects');
  
  // Default stats if none provided
  const displayStats = stats || [
    { label: 'Active Projects', value: projects.filter(p => p.status === 'ACTIVE').length },
    { label: 'Total Units', value: projects.reduce((sum, p) => sum + (p.totalUnits || 0), 0) },
    { label: 'Sold', value: `${Math.round((projects.reduce((sum, p) => sum + (p.soldUnits || 0), 0) / Math.max(projects.reduce((sum, p) => sum + (p.totalUnits || 0), 0), 1)) * 100)}%` },
  ];

  return (
    <div className="relative overflow-hidden bg-white min-h-screen flex items-center [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
      {/* Content */}
      <div className="relative w-full">
        <ProjectHeroCarousel
          projects={projects}
          stats={displayStats}
          {...(ctaText && { ctaText })}
          {...(ctaLink && { ctaLink })}
          messages={{
            badge: heroBadge,
            title: heroTitle,
            subtitle: heroSubtitle,
            viewProject: safeGetString(heroMsg?.viewProject || cardMsg?.viewProject, 'View Project'),
            availableUnits: safeGetString(heroMsg?.availableUnits || cardMsg?.availableUnits, 'Available'),
            soldOut: safeGetString(heroMsg?.soldOut || cardMsg?.soldOut, 'Sold Out'),
            comingSoon: safeGetString(heroMsg?.comingSoon || cardMsg?.comingSoon, 'Coming Soon'),
            startingFrom: safeGetString(heroMsg?.startingFrom || cardMsg?.startingFrom, 'Starting from'),
          }}
          locale={locale}
          maxProjects={maxProjects}
          {...(onProjectClick && { onProjectClick })}
          className="text-gray-900"
          textClassName="[&_h1]:[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] [&_h1]:text-[#243462] [&_h1]:font-bold [&_p]:[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] [&_p]:text-gray-700 [&_span.inline-flex]:!bg-gray-100 [&_span.inline-flex]:!text-[#243462] [&_span.inline-flex]:!border [&_span.inline-flex]:!border-gray-200 [&_span.inline-flex]:!bg-none [&_.font-bold]:text-[#BBA339]"
          carouselClassName="lg:pl-8"
        />
      </div>
    </div>
  );
}

